import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { 
  Globe, Bot, Megaphone, LayoutDashboard, ArrowRight, CheckCircle,
  Smartphone, ShoppingCart, FileText, Calendar, MessageSquare, 
  RefreshCcw, TrendingUp, BarChart3, Target, Zap
} from 'lucide-react';

const solutionCategories = [
  {
    id: 'sites',
    icon: Globe,
    title: 'Sites Web & Landing Pages Intelligentes',
    description: 'Des sites conçus pour convertir, pas juste pour exister. Focalisés sur la simplicité et les résultats.',
    color: 'primary',
    features: [
      { icon: Globe, title: 'Site vitrine', desc: 'Présentez votre activité de façon professionnelle et engageante.' },
      { icon: ShoppingCart, title: 'Site de commande', desc: 'Permettez à vos clients de commander ou réserver en ligne.' },
      { icon: FileText, title: 'Site de devis', desc: 'Générez des demandes de devis qualifiées automatiquement.' },
      { icon: Calendar, title: 'Pages de capture', desc: 'Landing pages optimisées pour la prise de RDV et la génération de leads.' },
    ],
    benefits: ['Optimisé mobile', 'Temps de chargement rapide', 'SEO friendly', 'Formulaires intelligents'],
  },
  {
    id: 'ia',
    icon: Bot,
    title: 'Assistants IA & Automatisation',
    description: 'Des systèmes autonomes qui travaillent pour vous 24/7. Pas un simple chatbot, un vrai assistant intelligent.',
    color: 'accent',
    features: [
      { icon: MessageSquare, title: 'Réponses automatiques', desc: 'Répondez instantanément à vos prospects, même la nuit.' },
      { icon: Target, title: 'Qualification des leads', desc: 'Identifiez automatiquement les prospects les plus chauds.' },
      { icon: FileText, title: 'Génération de devis', desc: 'Créez des devis personnalisés en quelques secondes.' },
      { icon: RefreshCcw, title: 'Relances intelligentes', desc: 'Ne perdez plus jamais un prospect avec des suivis automatisés.' },
    ],
    benefits: ['Disponible 24/7', 'Multilingue', 'Apprentissage continu', 'Intégration CRM'],
  },
  {
    id: 'acquisition',
    icon: Megaphone,
    title: 'Acquisition & Visibilité',
    description: 'Attirez les bons clients avec des campagnes ciblées et mesurables. ROI clair et transparent.',
    color: 'primary',
    features: [
      { icon: Target, title: 'Meta Ads', desc: 'Campagnes Facebook & Instagram optimisées pour la conversion.' },
      { icon: TrendingUp, title: 'Génération de leads', desc: 'Stratégies pour attirer des prospects qualifiés régulièrement.' },
      { icon: BarChart3, title: 'Tests d\'offres', desc: 'Validez vos offres avant d\'investir massivement.' },
      { icon: Zap, title: 'Scaling progressif', desc: 'Augmentez progressivement vos investissements selon les résultats.' },
    ],
    benefits: ['ROI mesuré', 'Ciblage précis', 'A/B testing', 'Reporting détaillé'],
  },
  {
    id: 'pilotage',
    icon: LayoutDashboard,
    title: 'Organisation & Pilotage',
    description: 'Centralisez, visualisez, décidez. Des outils simples pour garder le contrôle de votre activité.',
    color: 'accent',
    features: [
      { icon: LayoutDashboard, title: 'Centralisation des leads', desc: 'Tous vos prospects au même endroit, quel que soit le canal.' },
      { icon: BarChart3, title: 'Suivi simplifié', desc: 'Visualisez l\'état de chaque prospect en un coup d\'oeil.' },
      { icon: Target, title: 'Priorisation', desc: 'Concentrez-vous sur les actions à plus fort impact.' },
      { icon: TrendingUp, title: 'Tableaux de bord', desc: 'Des métriques claires pour prendre les bonnes décisions.' },
    ],
    benefits: ['Vision claire', 'Données en temps réel', 'Interface intuitive', 'Exports faciles'],
  },
];

const SolutionCard = ({ solution, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      id={solution.id}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="scroll-mt-32"
    >
      <div className="grid lg:grid-cols-2 gap-12 items-start">
        {/* Content */}
        <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
            solution.color === 'primary' ? 'bg-primary/10' : 'bg-accent/10'
          }`}>
            <solution.icon className={`w-8 h-8 ${
              solution.color === 'primary' ? 'text-primary' : 'text-accent'
            }`} />
          </div>

          <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-4">
            {solution.title}
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            {solution.description}
          </p>

          {/* Benefits */}
          <div className="flex flex-wrap gap-2 mb-8">
            {solution.benefits.map((benefit) => (
              <span
                key={benefit}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  solution.color === 'primary' 
                    ? 'bg-primary/10 text-primary' 
                    : 'bg-accent/10 text-accent'
                }`}
              >
                {benefit}
              </span>
            ))}
          </div>

          <Link to="/contact">
            <Button variant="hero" size="lg" className="btn-glow group">
              Discuter de mon projet
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Features Grid */}
        <div className={`grid sm:grid-cols-2 gap-4 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
          {solution.features.map((feature, featureIndex) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + featureIndex * 0.1 }}
              className="p-5 rounded-xl card-gradient border border-border/50 hover:border-primary/30 transition-all hover:shadow-glow"
            >
              <div className="feature-icon mb-3">
                <feature.icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-medium text-foreground mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default function SolutionsPage() {
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="hero-glow" />
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />

        <div className="section-container relative z-10">
          <motion.div
            ref={headerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="section-label mb-4 inline-block">
              Nos Solutions
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Des briques{' '}
              <span className="gradient-text">modulaires</span>
              {' '}pour votre croissance
            </h1>
            <p className="text-lg text-muted-foreground">
              Chaque solution peut fonctionner seule ou s'intégrer dans un système global. 
              On ne vous pousse rien — on construit ensemble ce dont vous avez vraiment besoin.
            </p>
          </motion.div>

          {/* Quick Nav */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 mt-12"
          >
            {solutionCategories.map((solution) => (
              <a
                key={solution.id}
                href={`#${solution.id}`}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/50 border border-border/50 text-sm font-medium text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
              >
                <solution.icon className="w-4 h-4" />
                {solution.title.split(' ')[0]}
              </a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solutions Sections */}
      <section className="py-20">
        <div className="section-container">
          <div className="space-y-32">
            {solutionCategories.map((solution, index) => (
              <SolutionCard key={solution.id} solution={solution} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-card to-secondary/20 border border-border/50 p-8 lg:p-16 text-center"
          >
            <div className="hero-glow" />
            <div className="relative z-10">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Vous ne savez pas par où commencer ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                C'est normal. C'est justement pour ça qu'on commence toujours par un diagnostic. 
                On analyse votre situation et on vous dit exactement ce qui a du sens pour vous.
              </p>
              <Link to="/contact">
                <Button variant="hero" size="xl" className="btn-glow group">
                  Demander mon diagnostic gratuit
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
